import React, { useContext, useEffect } from 'react';

import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';

import { UserContext } from 'lib/user-context';

import type { ZendeskProviderQuery } from './__generated__/ZendeskProviderQuery.graphql';

const ZENDESK_JWT_TOKEN_QUERY = graphql`
  query ZendeskProviderQuery {
    zendeskJwtToken {
      token
    }
  }
`;

export const ZendeskProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const zendeskKey = process.env.NEXT_PUBLIC_ZENDESK_KEY;
  const userInfo = useContext(UserContext);
  const relayEnvironment = useRelayEnvironment();

  useEffect(() => {
    if (!zendeskKey || !userInfo) return;

    // Load Zendesk Messaging SDK script
    const script = document.createElement('script');
    script.id = 'ze-snippet';
    script.src = `https://static.zdassets.com/ekr/snippet.js?key=${zendeskKey}`;
    script.async = true;

    // Wait for the script to load, then authenticate the user
    script.onload = () => {
      if (!window.zE) {
        return;
      }

      // Authenticate user with JWT using the loginUser API
      window.zE('messenger', 'loginUser', async function (callback: (token: string) => void) {
        try {
          // Fetch JWT token from backend via GraphQL
          const data = await fetchQuery<ZendeskProviderQuery>(
            relayEnvironment,
            ZENDESK_JWT_TOKEN_QUERY,
            {},
          ).toPromise();

          if (data?.zendeskJwtToken?.token) {
            callback(data.zendeskJwtToken.token);
          }
        } catch (error) {
          return;
        }
      });
    };

    document.body.appendChild(script);

    return () => {
      // Cleanup: logout user and remove script on unmount
      if (window.zE) {
        window.zE('messenger', 'logoutUser');
      }
      const existingScript = document.getElementById('ze-snippet');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [zendeskKey, userInfo, relayEnvironment]);

  return <>{children}</>;
};
